import { notification } from "antd";
import axios, { AxiosError, AxiosResponse } from "axios";

import { selectCurrentAdminToken } from "../utils/helpers/selectorHelper";
import ApiResponseDTO from "../shared/dtos/apiResponseDTO";

const apiUrl = process.env.REACT_APP_API_URL;
let globalAbortController: AbortController | null = null;

const axiosInstance = axios.create({
  baseURL: apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = selectCurrentAdminToken();
    if (token) config.headers!.auth = token;
    if (!globalAbortController) globalAbortController = new AbortController();
    return config;
  },
  (error) => {
    notification.error({ message: error, placement: "bottom" });
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, status } = response;

    const shouldHideSuccess = response.config.headers!["x-hide-success"];

    if (shouldHideSuccess) return response;

    const message =
      status === 201
        ? data.message ?? "Creado correctamente."
        : data.message ?? "Solicitud completada correctamente.";

    notification.success({
      message,
      placement: "bottom",
      key: "global",
    });

    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;
      const requestUrl = (error.response as any)?.request?.responseURL || '';
      const isAuth = requestUrl.includes("auth");
      const isUser = requestUrl.includes("user");

      if (status === 400)
        notification.error({
          message: isAuth
            ? "Correo y/o contraseña no coinciden!"
            : "No pudimos procesar su solicitud. Revise los datos e inténtelo nuevamente.",
          placement: "bottom",
          key: "global",
        });

      if (status === 401) {
        globalAbortController?.abort();
        globalAbortController = null;
        notification.error({
          message: isAuth
            ? "Correo y/o contraseña no coinciden!"
            : "No está autorizado para realizar esta acción.",
          placement: "bottom",
          key: "global",
        });
      }

      if (status === 403) {
        const responseData = data as ApiResponseDTO<void>;
        notification.error({
          message: responseData.message || "Acceso denegado.",
          placement: "bottom",
          key: "global",
        });
      }

      if (status === 409) {
        const response = data as ApiResponseDTO<void>;
        notification.error({
          message: isUser ? "Usuario ya existe con ese correo." : response.message,
          placement: "bottom",
          key: "global",
        });
      }

      if (status >= 500)
        notification.error({
          message: "Error del servidor, inténtelo más tarde.",
          placement: "bottom",
          key: "global",
        });
    } else if (error.request) {
      notification.error({
        message: "Error del servidor, inténtelo más tarde.",
        placement: "bottom",
        key: "global",
      });
    } else {
      notification.error({
        message: "Error del servidor, inténtelo más tarde.",
        description: error.message,
        placement: "bottom",
        key: "global",
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
